import {
    CloseButton,
    Combobox,
    Group,
    Pill,
    PillsInput,
    useCombobox,
} from '@mantine/core';
import { type ReactNode, useState } from 'react';
import type { Noop } from 'react-hook-form';
import { z } from 'zod';
import { filterByString } from '../../../utils/utilFunctions';
import { Show } from '../../control-flow/Show';

export const MultiSelectCreatableValueSchema = z.object({
    value: z.string(),
    label: z.string(),
});

export type MultiSelectCreatableValue = z.infer<
    typeof MultiSelectCreatableValueSchema
>;
export type MultiSelectCreatableData = MultiSelectCreatableValue & {
    userCreated: boolean;
};

export type Controlled_MultiSelectCreatableInputProps = {
    data: Array<MultiSelectCreatableData>;
    addOption: (newOption: MultiSelectCreatableData) => void;
    value: Array<MultiSelectCreatableValue>;
    onChange: (newValue: Array<MultiSelectCreatableValue>) => void;
    placeholder?: string;
    label?: ReactNode;
    disabled?: boolean;
    readOnly?: boolean;
    error?: ReactNode;
    onBlur?: Noop;
    name?: string;
};

export const Controlled_MultiSelectCreatableInput = ({
    data,
    addOption,
    value,
    onChange,
    placeholder,
    label,
    disabled,
    readOnly,
    error,
    onBlur,
    name,
}: Controlled_MultiSelectCreatableInputProps) => {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
        onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
    });

    const [search, setSearch] = useState('');

    const exactOptionMatch = data.some((item) => item.label === search);

    const getNewValue = (val: string, label: string) => ({
        label,
        value: `${val}_${window.crypto.randomUUID()}`,
    });

    const handleValueSelect = (val: string) => {
        if (val === '$create') {
            const valueBase = search.replaceAll('_', '-');
            const newValue = getNewValue(valueBase, search);

            addOption({ label: search, value: valueBase, userCreated: true });
            onChange([...value, newValue]);
        } else {
            const selected = data.find((it) => it.value === val);

            if (selected) {
                if (selected.userCreated) {
                    onChange([...value, getNewValue(val, selected.label)]);
                } else {
                    onChange([...value, selected]);
                }
            }
        }

        setSearch('');
    };

    const handleValueRemove = (val: string) =>
        onChange(value.filter((v) => v.value !== val));

    const values = value.map(({ value: itemValue, label: itemLabel }) => (
        <Pill
            key={itemValue}
            withRemoveButton
            onRemove={() => handleValueRemove(itemValue)}
        >
            {itemLabel}
        </Pill>
    ));

    const options = data.mapNotNull(
        ({ label: dataLabel, value: dataValue }) => {
            if (
                (Boolean(search) && !filterByString(dataLabel, search)) ||
                value.some((it) => it.value === dataValue)
            ) {
                return null;
            }

            return (
                <Combobox.Option
                    value={dataValue}
                    key={dataValue}
                >
                    <Group gap="sm">
                        <span>{dataLabel}</span>
                    </Group>
                </Combobox.Option>
            );
        }
    );

    return (
        <Combobox
            store={combobox}
            onOptionSubmit={handleValueSelect}
            withinPortal={true}
            disabled={disabled}
        >
            <Combobox.DropdownTarget>
                <PillsInput
                    onClick={() => combobox.openDropdown()}
                    rightSection={
                        <Group
                            justify={'flex-end'}
                            gap={6}
                            style={{
                                paddingRight: 4,
                                alignItems: 'center',
                            }}
                            w={'100%'}
                        >
                            <Show when={!readOnly && value.length}>
                                <CloseButton
                                    size="md"
                                    variant="transparent"
                                    onClick={() => {
                                        onChange([]);
                                    }}
                                />
                            </Show>
                        </Group>
                    }
                    rightSectionWidth={30}
                    onBlur={onBlur}
                    error={error}
                    label={label}
                >
                    <Pill.Group>
                        {values}

                        <Combobox.EventsTarget>
                            <PillsInput.Field
                                onFocus={() => combobox.openDropdown()}
                                onBlur={() => combobox.closeDropdown()}
                                value={search}
                                name={name}
                                placeholder={
                                    placeholder ?? 'Select or create values'
                                }
                                onChange={(event) => {
                                    combobox.updateSelectedOptionIndex();
                                    setSearch(event.currentTarget.value);
                                }}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Backspace' &&
                                        search.length === 0
                                    ) {
                                        event.preventDefault();
                                        value
                                            .at(-1)
                                            ?.let((it) =>
                                                handleValueRemove(it.value)
                                            );
                                    }
                                }}
                            />
                        </Combobox.EventsTarget>
                    </Pill.Group>
                </PillsInput>
            </Combobox.DropdownTarget>

            <Combobox.Dropdown>
                <Combobox.Options>
                    {options}

                    <Show when={!exactOptionMatch && search.trim().length}>
                        <Combobox.Option value="$create">
                            + Create '{search}'
                        </Combobox.Option>
                    </Show>
                    <Show
                        when={
                            exactOptionMatch &&
                            search.trim().length &&
                            !options.length
                        }
                    >
                        <Combobox.Empty>Nothing found</Combobox.Empty>
                    </Show>
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};
